import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum ScrapReason {
  DEFECTIVE_MATERIAL   = 'defective_material',
  PROCESS_ERROR        = 'process_error',
  EQUIPMENT_MALFUNCTION = 'equipment_malfunction',
  OPERATOR_ERROR       = 'operator_error',
  DESIGN_ISSUE         = 'design_issue',
  DAMAGE               = 'damage',
  OBSOLESCENCE         = 'obsolescence',
  EXCESS_INVENTORY     = 'excess_inventory',
}

export enum ScrapDisposition {
  SCRAP                = 'scrap',
  REWORK               = 'rework',
  USE_AS_IS            = 'use_as_is',
  RETURN_TO_SUPPLIER   = 'return_to_supplier',
  RECLAIM_MATERIAL     = 'reclaim_material',
}

export enum ScrapStatus {
  PENDING  = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum ReworkStatus {
  PENDING     = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED   = 'completed',
  CANCELLED   = 'cancelled',
}

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface ScrapCost {
  materialCost:   number;
  laborCost:      number;
  overheadCost:   number;
  totalCost:      number;
  recoveredValue?: number;
  netLoss:        number;
}

export interface ScrapRecord {
  id:           string;
  itemId:       string;
  batchNumber?: string;
  serialNumber?: string;
  quantity:     number;
  scrapReason:  ScrapReason;
  defectType:   string;
  rootCause?:   string;
  scrapDate:    string;
  reportedBy:   string;
  approvedBy?:  string;
  costs:        ScrapCost;
  disposition:  ScrapDisposition;
  status:       ScrapStatus;
  photos?:      string[];
  notes?:       string;
}

export interface ReworkOrder {
  id:                  string;
  scrapRecordId:       string;
  reworkInstructions:  string;
  estimatedCost:       number;
  estimatedTime:       number;
  assignedTo:          string;
  status:              ReworkStatus;
  actualCost?:         number;
  actualTime?:         number;
  completionNotes?:    string;
}

export interface ScrapAnalytics {
  totalScrapCost:   number;
  recoveredValue:   number;
  netLoss:          number;
  costTrend:        Array<{ date: string; cost: number }>;
  reasonBreakdown:  Array<{ reason: string; count: number; cost: number }>;
}

// ─── State ────────────────────────────────────────────────────────────────────

interface ScrapState {
  scrapRecords:  ScrapRecord[];
  reworkOrders:  ReworkOrder[];
  analytics:     ScrapAnalytics | null;
  isLoading:     boolean;
  error:         string | null;
  /** true when the backend scrap service is unavailable (404 / 503) */
  serviceUnavailable: boolean;
}

const initialState: ScrapState = {
  scrapRecords:       [],
  reworkOrders:       [],
  analytics:          null,
  isLoading:          false,
  error:              null,
  serviceUnavailable: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/** Returns true for status codes that indicate the service doesn't exist yet. */
function isServiceDown(status: number): boolean {
  return status === 404 || status === 503 || status === 502 || status === 0;
}

function normaliseScrapRecord(raw: any): ScrapRecord {
  const costs = raw.costs ?? {};
  const mat   = Number(costs.materialCost ?? costs.material_cost ?? 0);
  const lab   = Number(costs.laborCost    ?? costs.labor_cost    ?? 0);
  const ovh   = Number(costs.overheadCost ?? costs.overhead_cost ?? 0);
  return {
    id:           String(raw.id ?? raw._id ?? ''),
    itemId:       raw.itemId ?? raw.item_id ?? raw.item ?? '',
    batchNumber:  raw.batchNumber ?? raw.batch_number,
    serialNumber: raw.serialNumber ?? raw.serial_number,
    quantity:     Number(raw.quantity ?? 0),
    scrapReason:  raw.scrapReason ?? raw.scrap_reason ?? ScrapReason.PROCESS_ERROR,
    defectType:   raw.defectType ?? raw.defect_type ?? '',
    rootCause:    raw.rootCause ?? raw.root_cause,
    scrapDate:    raw.scrapDate ?? raw.scrap_date ?? raw.createdAt ?? '',
    reportedBy:   raw.reportedBy ?? raw.reported_by ?? '',
    approvedBy:   raw.approvedBy ?? raw.approved_by,
    costs: {
      materialCost:   mat,
      laborCost:      lab,
      overheadCost:   ovh,
      totalCost:      Number(costs.totalCost ?? costs.total_cost ?? mat + lab + ovh),
      recoveredValue: costs.recoveredValue != null ? Number(costs.recoveredValue) : undefined,
      netLoss:        Number(costs.netLoss  ?? costs.net_loss ?? mat + lab + ovh),
    },
    disposition:  raw.disposition ?? ScrapDisposition.SCRAP,
    status:       raw.status      ?? ScrapStatus.PENDING,
    photos:       Array.isArray(raw.photos) ? raw.photos : undefined,
    notes:        raw.notes,
  };
}

function normaliseReworkOrder(raw: any): ReworkOrder {
  return {
    id:                 String(raw.id ?? raw._id ?? ''),
    scrapRecordId:      raw.scrapRecordId ?? raw.scrap_record_id ?? '',
    reworkInstructions: raw.reworkInstructions ?? raw.rework_instructions ?? '',
    estimatedCost:      Number(raw.estimatedCost ?? raw.estimated_cost ?? 0),
    estimatedTime:      Number(raw.estimatedTime ?? raw.estimated_time ?? 0),
    assignedTo:         raw.assignedTo ?? raw.assigned_to ?? '',
    status:             raw.status ?? ReworkStatus.PENDING,
    actualCost:         raw.actualCost != null ? Number(raw.actualCost) : undefined,
    actualTime:         raw.actualTime != null ? Number(raw.actualTime) : undefined,
    completionNotes:    raw.completionNotes ?? raw.completion_notes,
  };
}

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchScrapRecords = createAsyncThunk(
  'scrap/fetchRecords',
  async (params: { dateFrom?: string; dateTo?: string; scrapReason?: string; status?: string } = {}, { rejectWithValue }) => {
    try {
      const qs = new URLSearchParams();
      if (params.dateFrom)    qs.set('dateFrom',    params.dateFrom);
      if (params.dateTo)      qs.set('dateTo',      params.dateTo);
      if (params.scrapReason) qs.set('scrapReason', params.scrapReason);
      if (params.status)      qs.set('status',      params.status);
      const url = `${API_BASE}/scrap/records${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (isServiceDown(res.status)) {
        return rejectWithValue({ serviceUnavailable: true });
      }
      if (!res.ok) {
        return rejectWithValue({ message: `Failed to load scrap records (${res.status})` });
      }
      const data = await res.json();
      const raw: any[] = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      return raw.map(normaliseScrapRecord);
    } catch (err: any) {
      return rejectWithValue({ serviceUnavailable: true });
    }
  }
);

export const createScrapRecord = createAsyncThunk(
  'scrap/createRecord',
  async (payload: Partial<ScrapRecord>, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/scrap/records`, {
        method:  'POST',
        headers: authHeaders(),
        body:    JSON.stringify(payload),
      });
      if (isServiceDown(res.status)) return rejectWithValue({ serviceUnavailable: true });
      if (!res.ok) return rejectWithValue({ message: `Failed to create scrap record (${res.status})` });
      const data = await res.json();
      return normaliseScrapRecord(data?.data ?? data);
    } catch {
      return rejectWithValue({ serviceUnavailable: true });
    }
  }
);

export const approveScrapRecord = createAsyncThunk(
  'scrap/approveRecord',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/scrap/records/${id}`, {
        method:  'PATCH',
        headers: authHeaders(),
        body:    JSON.stringify({ status: 'approved' }),
      });
      if (isServiceDown(res.status)) return rejectWithValue({ serviceUnavailable: true });
      if (!res.ok) return rejectWithValue({ message: `Failed to approve scrap record (${res.status})` });
      const data = await res.json();
      return normaliseScrapRecord(data?.data ?? data);
    } catch {
      return rejectWithValue({ serviceUnavailable: true });
    }
  }
);

export const createReworkOrder = createAsyncThunk(
  'scrap/createReworkOrder',
  async (payload: Partial<ReworkOrder>, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/scrap/rework`, {
        method:  'POST',
        headers: authHeaders(),
        body:    JSON.stringify(payload),
      });
      if (isServiceDown(res.status)) return rejectWithValue({ serviceUnavailable: true });
      if (!res.ok) return rejectWithValue({ message: `Failed to create rework order (${res.status})` });
      const data = await res.json();
      return normaliseReworkOrder(data?.data ?? data);
    } catch {
      return rejectWithValue({ serviceUnavailable: true });
    }
  }
);

export const fetchReworkOrders = createAsyncThunk(
  'scrap/fetchReworkOrders',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/scrap/rework`, { headers: authHeaders() });
      if (isServiceDown(res.status)) return rejectWithValue({ serviceUnavailable: true });
      if (!res.ok) return rejectWithValue({ message: `Failed to load rework orders (${res.status})` });
      const data = await res.json();
      const raw: any[] = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      return raw.map(normaliseReworkOrder);
    } catch {
      return rejectWithValue({ serviceUnavailable: true });
    }
  }
);

export const updateReworkStatus = createAsyncThunk(
  'scrap/updateReworkStatus',
  async ({ id, status, completionNotes }: { id: string; status: string; completionNotes?: string }, { rejectWithValue }) => {
    const token = localStorage.getItem('token') ?? '';
    const res = await fetch(`${API_BASE}/scrap/rework/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status, completionNotes }),
    });
    if (!res.ok) return rejectWithValue(await res.text());
    return { id, status };
  }
);

export const rejectScrapRecord = createAsyncThunk(
  'scrap/rejectRecord',
  async ({ id, rejectionReason }: { id: string; rejectionReason: string }, { rejectWithValue }) => {
    const token = localStorage.getItem('token') ?? '';
    const res = await fetch(`${API_BASE}/scrap/records/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: 'rejected', rejectionReason }),
    });
    if (!res.ok) return rejectWithValue(await res.text());
    return { id };
  }
);

export const fetchScrapAnalytics = createAsyncThunk(
  'scrap/fetchAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE}/scrap/analytics`, { headers: authHeaders() });
      if (isServiceDown(res.status)) return rejectWithValue({ serviceUnavailable: true });
      if (!res.ok) return rejectWithValue({ message: `Failed to load analytics (${res.status})` });
      const data = await res.json();
      const d = data?.data ?? data;
      return {
        totalScrapCost:  Number(d.totalScrapCost  ?? d.total_scrap_cost  ?? 0),
        recoveredValue:  Number(d.recoveredValue   ?? d.recovered_value   ?? 0),
        netLoss:         Number(d.netLoss          ?? d.net_loss          ?? 0),
        costTrend:       Array.isArray(d.costTrend ?? d.cost_trend) ? (d.costTrend ?? d.cost_trend) : [],
        reasonBreakdown: Array.isArray(d.reasonBreakdown ?? d.reason_breakdown) ? (d.reasonBreakdown ?? d.reason_breakdown) : [],
      } as ScrapAnalytics;
    } catch {
      return rejectWithValue({ serviceUnavailable: true });
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const scrapSlice = createSlice({
  name: 'scrap',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearServiceUnavailable: (state) => {
      state.serviceUnavailable = false;
    },
  },
  extraReducers: (builder) => {
    // fetchScrapRecords
    builder
      .addCase(fetchScrapRecords.pending, (state) => {
        state.isLoading = true;
        state.error     = null;
      })
      .addCase(fetchScrapRecords.fulfilled, (state, action) => {
        state.isLoading          = false;
        state.scrapRecords       = action.payload;
        state.serviceUnavailable = false;
      })
      .addCase(fetchScrapRecords.rejected, (state, action) => {
        state.isLoading = false;
        const p = action.payload as any;
        if (p?.serviceUnavailable) {
          state.serviceUnavailable = true;
          state.scrapRecords = [];
        } else {
          state.error = p?.message ?? 'Failed to load scrap records';
        }
      });

    // createScrapRecord
    builder
      .addCase(createScrapRecord.fulfilled, (state, action) => {
        state.scrapRecords.unshift(action.payload);
      })
      .addCase(createScrapRecord.rejected, (state, action) => {
        const p = action.payload as any;
        state.error = p?.message ?? 'Failed to create scrap record';
      });

    // approveScrapRecord
    builder
      .addCase(approveScrapRecord.fulfilled, (state, action) => {
        const idx = state.scrapRecords.findIndex(r => r.id === action.payload.id);
        if (idx !== -1) state.scrapRecords[idx] = action.payload;
      })
      .addCase(approveScrapRecord.rejected, (state, action) => {
        const p = action.payload as any;
        state.error = p?.message ?? 'Failed to approve record';
      });

    // createReworkOrder
    builder
      .addCase(createReworkOrder.fulfilled, (state, action) => {
        state.reworkOrders.unshift(action.payload);
      })
      .addCase(createReworkOrder.rejected, (state, action) => {
        const p = action.payload as any;
        state.error = p?.message ?? 'Failed to create rework order';
      });

    // fetchReworkOrders
    builder
      .addCase(fetchReworkOrders.fulfilled, (state, action) => {
        state.reworkOrders       = action.payload;
        state.serviceUnavailable = false;
      })
      .addCase(fetchReworkOrders.rejected, (state, action) => {
        const p = action.payload as any;
        if (p?.serviceUnavailable) {
          state.serviceUnavailable = true;
          state.reworkOrders = [];
        } else {
          state.error = p?.message ?? 'Failed to load rework orders';
        }
      });

    // updateReworkStatus
    builder
      .addCase(updateReworkStatus.fulfilled, (state, action) => {
        const idx = state.reworkOrders.findIndex(r => r.id === action.payload.id);
        if (idx !== -1) {
          state.reworkOrders[idx] = { ...state.reworkOrders[idx], status: action.payload.status as ReworkStatus };
        }
      })
      .addCase(updateReworkStatus.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to update rework status';
      });

    // rejectScrapRecord
    builder
      .addCase(rejectScrapRecord.fulfilled, (state, action) => {
        const idx = state.scrapRecords.findIndex(r => r.id === action.payload.id);
        if (idx !== -1) {
          state.scrapRecords[idx] = { ...state.scrapRecords[idx], status: ScrapStatus.REJECTED };
        }
      })
      .addCase(rejectScrapRecord.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to reject scrap record';
      });

    // fetchScrapAnalytics
    builder
      .addCase(fetchScrapAnalytics.fulfilled, (state, action) => {
        state.analytics          = action.payload;
        state.serviceUnavailable = false;
      })
      .addCase(fetchScrapAnalytics.rejected, (state, action) => {
        const p = action.payload as any;
        if (p?.serviceUnavailable) {
          state.serviceUnavailable = true;
        }
        state.analytics = null;
      });
  },
});

export const { clearError, clearServiceUnavailable } = scrapSlice.actions;
export default scrapSlice.reducer;
